import ComponentRegistry from './ComponentRegistry';

class SidecarBridge {
  constructor() {
    this.channel = new BroadcastChannel('kh-sidecar');
    this.setupHandlers();
  }

  setupHandlers() {
    this.channel.onmessage = (event) => {
      if (event.data.source === 'sidecar') {
        this.handleSidecarMessage(event.data);
      }
    };
  }

  handleSidecarMessage(message) {
    const { type, messageId, payload } = message;
    if (messageId) {
      this.handleRpcRequest(type, payload, messageId);
      return;
    }
    this.handleBroadcast(type, payload);
  }

  handleRpcRequest(type, payload, messageId) {
    let response = null;
    let error = null;
    try {
      switch (type) {
        case 'GET_APP_STATUS':
          response = { status: 'running', version: '1.0.0', user: this.getCurrentUser(), timestamp: Date.now() }; break;
        case 'GET_USER_INFO':
          response = { id: 123, name: 'Test User', role: 'admin', permissions: ['read', 'write', 'admin'] }; break;
        case 'GET_CONFIG':
          const section = payload?.section || 'general'; response = this.getConfig(section); break;
        case 'GET_DRAWING_OBJECTS':
          response = this.getDrawingObjects(payload); break;
        case 'GET_DRAWING_OBJECTS_IN_HIERARCHY':
          response = this.getDrawingObjectsInHierarchy(payload); break;
        case 'GET_AVAILABLE_COMPONENTS':
          response = { components: ComponentRegistry.getRegistered(), imageViewer: ComponentRegistry.has('ImageViewer') }; break;
        case 'ZOOM_TO_OBJECT':
          response = this.zoomToObject(payload); break;
        case 'UPDATE_HIERARCHY_NODE_ATTRIBUTE':
          response = this.updateHierarchyNodeAttribute(payload); break;
        default:
          error = `Unknown RPC command: ${type}`;
      }
    } catch (err) { error = err.message; }

    // Promise-aware response handling
    if (response && typeof response.then === 'function') {
      response
        .then(res => this.sendResponse(type, res, null, messageId))
        .catch(err => this.sendResponse(type, null, err?.message || 'Unknown error', messageId));
      return; // prevent double send
    }
    this.sendResponse(type, response, error, messageId);
  }

  handleBroadcast(type, payload) {
    console.log(`[SidecarBridge] Received broadcast: ${type}`, payload);
    
    switch (type) {
      case 'SIDECAR_READY':
        console.log('Sidecar is ready');
        // Could trigger some initialization
        break;
      
      case 'USER_ACTION':
        console.log('User action from sidecar:', payload);
        // Handle user actions from sidecar
        break;
    }
  }

  sendResponse(type, response, error, replyTo) {
    const message = {
      type: `${type}_RESPONSE`,
      payload: error ? { error } : response,
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
      source: 'main-app',
      replyTo
    };
    this.channel.postMessage(message);
  }

  // Public method for main app to broadcast to sidecar
  broadcast(type, payload = {}) {
    const message = {
      type,
      payload,
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
      source: 'main-app'
    };
    this.channel.postMessage(message);
  }

  getCurrentUser() {
    // Mock implementation - replace with real user data
    return 'current_user';
  }

  getConfig(section) {
    // Mock implementation - replace with real config
    const configs = {
      ui: {
        theme: 'dark',
        language: 'en',
        features: ['search', 'export', 'notifications']
      },
      api: {
        baseUrl: '/api/v1',
        timeout: 30000
      },
      general: {
        appName: 'Knowledge Hub',
        version: '1.0.0'
      }
    };
    
    return configs[section] || { error: `Config section '${section}' not found` };
  }

  _buildObjectsResponse({annotationObjects, filteredObjects, payload, imageViewer, enhanceItem}) {
    const canvas = imageViewer.canvas;
    const includeCrops = payload?.includeCrops !== false;
    let bgImg = canvas?.backgroundImage;
    let imgElement = bgImg?._element || (bgImg && bgImg.getElement && bgImg.getElement());
    let naturalW = imgElement?.naturalWidth || imgElement?.width;
    let naturalH = imgElement?.naturalHeight || imgElement?.height;
    let scaledW = bgImg?.getScaledWidth ? bgImg.getScaledWidth() : naturalW;
    let scaledH = bgImg?.getScaledHeight ? bgImg.getScaledHeight() : naturalH;
    let scaleX = scaledW && naturalW ? scaledW / naturalW : 1;
    let scaleY = scaledH && naturalH ? scaledH / naturalH : 1;
    let offX = bgImg?.left || 0;
    let offY = bgImg?.top || 0;
    let tempCanvas = document.createElement('canvas');
    let tempCtx = tempCanvas.getContext('2d');

    const objectsWithCrops = filteredObjects.map(obj => {
      const base = {
        id: obj.id,
        label: obj.label,
        text: obj.text,
        bbox: obj.bbox,
        metadata: obj.metadata
      };
      const result = enhanceItem ? enhanceItem(base, obj) || base : base;
      if (includeCrops && obj.bbox && imgElement && naturalW && naturalH) {
        try {
          const { x1, y1, x2, y2 } = obj.bbox;
          let sx = Math.round((x1 - offX) / scaleX);
          let sy = Math.round((y1 - offY) / scaleY);
          let sw = Math.round((x2 - x1) / scaleX);
          let sh = Math.round((y2 - y1) / scaleY);
          let pad = Math.round(Math.min(sw, sh) * 0.1);
          sx = Math.max(0, sx - pad);
          sy = Math.max(0, sy - pad);
          sw = Math.min(naturalW - sx, sw + 2 * pad);
          sh = Math.min(naturalH - sy, sh + 2 * pad);
          if (sw <= 0 || sh <= 0) throw new Error('Invalid crop size');
          const maxThumb = 160;
          const ratio = Math.min(1, maxThumb / Math.max(sw, sh));
          const destW = Math.max(1, Math.round(sw * ratio));
          const destH = Math.max(1, Math.round(sh * ratio));
          tempCanvas.width = destW;
          tempCanvas.height = destH;
          tempCtx.clearRect(0, 0, destW, destH);
          tempCtx.drawImage(
            imgElement,
            sx, sy, sw, sh,
            0, 0, destW, destH
          );
          result.crop = tempCanvas.toDataURL('image/jpeg', 0.8);
          result.cropDimensions = { width: destW, height: destH };
        } catch (error) {
          result.cropError = 'Failed to create crop';
        }
      }
      return result;
    });

    return {
      total: annotationObjects.length,
      filtered: filteredObjects.length,
      objects: objectsWithCrops
    };
  }

  getDrawingObjects(payload) {
    const imageViewer = ComponentRegistry.get('ImageViewer');
    if (!imageViewer || !imageViewer.state?.baseAnnotation) {
      return {
        error: 'ImageViewer not available or no annotation loaded',
        available_components: ComponentRegistry.getRegistered()
      };
    }
    const annotation = imageViewer.state.baseAnnotation;
    const objects = annotation.objects || [];
    let filteredObjects = objects;
    if (payload?.filters) {
      const { labelFilter, textFilter } = payload.filters;
      if (labelFilter) {
        filteredObjects = filteredObjects.filter(obj =>
          obj.label && obj.label.toLowerCase().includes(labelFilter.toLowerCase())
        );
      }
      if (textFilter) {
        filteredObjects = filteredObjects.filter(obj =>
          obj.text && obj.text.toLowerCase().includes(textFilter.toLowerCase())
        );
      }
    }
    return this._buildObjectsResponse({annotationObjects: objects, filteredObjects, payload, imageViewer});
  }

  getDrawingObjectsInHierarchy(payload) {
    const imageViewer = ComponentRegistry.get('ImageViewer');
    if (!imageViewer || !imageViewer.state?.baseAnnotation) {
      return {
        error: 'ImageViewer not available or no annotation loaded',
        available_components: ComponentRegistry.getRegistered()
      };
    }

    const viewHierarchy = imageViewer.getViewHierarchy ? imageViewer.getViewHierarchy() : null;
    const hierarchy = viewHierarchy?.state?.hierarchy ?? null;
    if (!hierarchy) {
      return { error: 'Hierarchy not loaded' };
    }

    const annotation = imageViewer.state.baseAnnotation;
    const objects = annotation.objects || [];

    let filteredObjects = objects.filter(obj => hierarchy.findNodeByObject && hierarchy.findNodeByObject(obj));

    if (payload?.filters) {
      const { labelFilter, textFilter } = payload.filters;
      if (labelFilter) {
        filteredObjects = filteredObjects.filter(obj =>
          obj.label && obj.label.toLowerCase().includes(labelFilter.toLowerCase())
        );
      }
      if (textFilter) {
        filteredObjects = filteredObjects.filter(obj =>
          obj.text && obj.text.toLowerCase().includes(textFilter.toLowerCase())
        );
      }
    }

    return this._buildObjectsResponse({
      annotationObjects: objects,
      filteredObjects,
      payload,
      imageViewer,
      enhanceItem: (base, obj) => {
        const node = hierarchy.findNodeByObject(obj);
        if (!node) return base;
        const parent = node.getParent && node.getParent();
        const parentFlocAttr = parent ? (parent.attributes || []).find(a => a.key === 'FUNCTIONAL LOCATION') : null;
        return {
          ...base,
          hierarchyNodeId: node.id,
          hierarchyNodeAttributes: (node.attributes || []).map(a => ({ key: a.key, value: a.value })),
          superiorFunctionalLocation: parentFlocAttr ? parentFlocAttr.value : null
        };
      }
    });
  }

  zoomToObject(payload) {
    const imageViewer = ComponentRegistry.get('ImageViewer');
    
    if (!imageViewer || !imageViewer.canvas) {
      return { error: 'ImageViewer not available' };
    }

    const { objectId } = payload;
    if (!objectId) {
      return { error: 'Object ID is required' };
    }

    try {
      // Найдем объект на canvas по ID
      const canvasObject = imageViewer.canvas.getObjects()
        .find(obj => obj.isProperObject && obj.objectMetadata?.id === objectId);

      if (!canvasObject) {
        return { error: `Object with ID ${objectId} not found on canvas` };
      }

      // Используем zoomHandler для зума к объекту
      if (imageViewer.zoomHandler && imageViewer.zoomHandler.zoomToObject) {
        imageViewer.zoomHandler.zoomToObject(canvasObject);
        return { success: true, message: `Zoomed to object ${objectId}` };
      } else {
        return { error: 'Zoom functionality not available' };
      }
    } catch (error) {
      return { error: `Failed to zoom to object: ${error.message}` };
    }
  }

  updateHierarchyNodeAttribute(payload) {
    const { nodeId, key, value } = payload || {};
    if (!nodeId || !key) return { error: 'nodeId and key required' };
    const imageViewer = ComponentRegistry.get('ImageViewer');
    const viewHierarchy = imageViewer?.getViewHierarchy ? imageViewer.getViewHierarchy() : null;
    const hierarchy = viewHierarchy?.state?.hierarchy ?? null;
    if (!hierarchy) return { error: 'Hierarchy not loaded' };
    const node = hierarchy.getNodeById ? hierarchy.getNodeById(nodeId) : null;
    if (!node) return { error: 'Node not found' };

    // Decide whether to add or update
    const hasAttr = node.attributes?.some(a => a.key === key);
    let promise;
    if (hasAttr && node.updateAttribute) {
      promise = node.updateAttribute({ key, value });
    } else if (!hasAttr && node.addAttribute) {
      promise = node.addAttribute({ key, value });
    } else {
      // fallback optimistic if methods not available
      const attr = node.attributes?.find(a => a.key === key);
      if (attr) attr.value = value; else (node.attributes || (node.attributes = [])).push({ key, value });
      return { success: true, nodeId, key, value, optimistic: true };
    }

    // After backend update & hierarchy reload, return fresh attribute list
    return promise.then(() => {
      const freshNode = hierarchy.getNodeById ? hierarchy.getNodeById(nodeId) : node;
      const attrs = freshNode?.attributes || [];
      const updatedAttr = attrs.find(a => a.key === key) || { key, value };
      return { success: true, nodeId, key: updatedAttr.key, value: updatedAttr.value, attributes: attrs };
    }).catch(err => ({ error: err?.message || 'Failed to update attribute' }));
  }

  generateMessageId() {
    return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }
}

export default new SidecarBridge();
